import { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import api, { nzISODate } from "@/lib/api";
import { errMsg } from "@/lib/hsUtil";
import Gantt from "./programme/Gantt";
import ItemEditor from "./programme/ItemEditor";
import ThisWeek from "./programme/ThisWeek";

export function parseFlexibleDate(input) {
  if (!input) return { iso: "", err: "" };
  const s = String(input).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return { iso: s, err: "" };
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
  if (!m) return { iso: "", err: "Enter DD/MM/YYYY" };
  const y = m[3].length === 2 ? `20${m[3]}` : m[3];
  return { iso: `${y}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`, err: "" };
}

const AddTask = ({ pid, onAdded }) => {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  async function add(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    const today = nzISODate();
    try {
      await api.post("/programme-items", { project_id: pid, name: name.trim(), start_date: today, end_date: today, duration_days: 1 });
      setName(""); toast.success("Task added. Click it in the chart to set dates."); onAdded();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  }
  return (
    <form onSubmit={add} className="flex gap-2 items-center">
      <input className="input !w-auto" placeholder="Add a single task" value={name} onChange={e => setName(e.target.value)} data-testid="prog-f-name"/>
      <button className="btn-ghost text-sm" disabled={busy} data-testid="btn-add-programme">+ Add</button>
    </form>
  );
};

export default function ProgrammeTab() {
  const { project } = useOutletContext();
  const [items, setItems] = useState([]);
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rk, setRk] = useState(0);
  const fileRef = useRef(null);
  const load = () => api.get(`/projects/${project.id}/programme`).then(r => { setItems(r.data.items); setRk(k => k + 1); });
  useEffect(() => { load(); }, [project.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function onFile(e) {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/programme/import`, fd);
      toast.success(`Programme imported: ${data.added} added, ${data.updated} updated, ${data.unchanged} unchanged`
        + (data.errors.length ? `. ${data.errors.length} row(s) skipped: ${data.errors.slice(0, 3).join("; ")}` : ""), { duration: 9000 });
      load();
    } catch (err) { toast.error(errMsg(err, "Import failed")); } finally { setBusy(false); }
  }
  async function pdf() {
    const r = await api.get(`/projects/${project.id}/programme.pdf`, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  }
  return (
    <div className="space-y-3" data-testid="programme-tab">
      <div className="card flex flex-wrap items-center gap-2">
        <h3 className="text-silver mr-2">Programme</h3>
        <button className="btn-silver" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="programme-import-btn">{busy ? "Importing…" : "Import CSV"}</button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="programme-import-input"/>
        {items.length > 0 && <button className="btn-ghost text-sm" onClick={pdf} data-testid="programme-pdf-btn">Download PDF</button>}
        <AddTask pid={project.id} onAdded={load}/>
        <div className="text-xs opacity-60 w-full">CSV columns: ID, Phase, Activity, Trade / responsibility, Start, Finish, Duration (working days), Predecessors, Notes. Re-importing updates rows by ID. Click a row in the chart to change dates or tick it done.</div>
      </div>
      <ThisWeek pid={project.id} refreshKey={rk}/>
      {items.length === 0 ? <div className="card opacity-60" data-testid="programme-empty">No programme yet. Import the programme CSV to build the chart.</div>
        : <Gantt items={items} todayIso={nzISODate()} onPick={setPick}/>}
      {pick && <ItemEditor item={pick} onClose={() => setPick(null)} onSaved={() => { setPick(null); load(); }}/>}
    </div>
  );
}
